import useSuitesProjectsLanguage from './useSuitesProjectsLanguage';
import StatusIndicator from './test-suites/StatusIndicator';
import SuiteCreateDialog from './test-suites/SuiteCreateDialog';
import SuiteAddTestsDialog from './test-suites/SuiteAddTestsDialog';
import SuiteRunResultsDialog from './test-suites/SuiteRunResultsDialog';
import SuiteConfigDialog from './test-suites/SuiteConfigDialog';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ListChecks,
  Plus,
  Play,
  Trash2,
  Edit2,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Search,
  MoreHorizontal,
  Workflow,
  FlaskConical,
  RefreshCw,
  X,
  History,
  Loader2,
  FolderOpen,
  Layers,
  ArrowRight,
  Settings2,
  GripVertical,
  Monitor,
  Smartphone,
  Tablet,
  Globe
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { getBackendURL } from '../utils/backendURL';

const TestSuites = ({ user }) => {
  const { t, language } = useSuitesProjectsLanguage();
  const [suites, setSuites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddTestModal, setShowAddTestModal] = useState(false);
  const [showRunResultModal, setShowRunResultModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  
  // Selected suite for detail view
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    execution_mode: 'continue_on_failure',
    suite_type: 'flow_testing'
  });
  
  // Flow configuration
  const [flowConfig, setFlowConfig] = useState({
    browser: 'chromium',
    device_type: 'desktop',
    viewport_width: 1920,
    viewport_height: 1080,
    is_headless: true,
    environment_id: null
  });
  
  // Available environments
  const [availableEnvironments, setAvailableEnvironments] = useState([]);
  
  // Available tests
  const [availableApiTests, setAvailableApiTests] = useState([]);
  const [availableFlowTests, setAvailableFlowTests] = useState([]);
  const [testSearchQuery, setTestSearchQuery] = useState('');
  const [testTypeFilter, setTestTypeFilter] = useState('all');
  
  // Running state
  const [runningSuiteId, setRunningSuiteId] = useState(null);
  const [runProgress, setRunProgress] = useState(null);
  
  // Runs pagination state
  const [displayedRunsCount, setDisplayedRunsCount] = useState(5);
  const [loadingMoreRuns, setLoadingMoreRuns] = useState(false);
  const [allRuns, setAllRuns] = useState([]);
  const [totalRunsCount, setTotalRunsCount] = useState(0);
  
  const BACKEND_URL = getBackendURL();

  const fetchSuites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const token = localStorage.getItem('token');
      if (!token) {
        // No token - user not logged in, don't show error
        setSuites([]);
        return;
      }
      const response = await axios.get(`${BACKEND_URL}/api/test-suites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuites(response.data.suites || []);
    } catch (err) {
      console.error('Failed to fetch suites:', err);
      // Only show error for actual API failures, not auth issues
      if (err.response?.status === 401) {
        // Token expired - let the app's auth handler deal with this
        // Don't show error banner, just set empty state
        setSuites([]);
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
        setSuites([]);
      } else if (!err.response) {
        setError('Network error. Please check your connection.');
        setSuites([]);
      } else {
        // For other cases (404, etc.), just set empty array without error
        setSuites([]);
      }
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  const fetchAvailableTests = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [apiResponse, flowResponse, envResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/test-suites/available/api-tests?limit=200`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/test-suites/available/flow-tests?limit=200`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/test-suites/available/environments`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAvailableApiTests(apiResponse.data.tests || []);
      setAvailableFlowTests(flowResponse.data.tests || []);
      setAvailableEnvironments(envResponse.data.environments || []);
    } catch (err) {
      console.error('Failed to fetch available tests:', err);
    }
  };

  const fetchSuiteDetails = async (suiteId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/test-suites/${suiteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSuite(response.data);
      
      // Load the suite's flow config
      if (response.data.flow_config) {
        setFlowConfig(response.data.flow_config);
      }
      
      // Reset runs pagination when selecting a new suite
      setDisplayedRunsCount(5);
      setAllRuns(response.data.recent_runs || []);
      setTotalRunsCount(response.data.total_runs_count || response.data.recent_runs?.length || 0);
      
      await fetchAvailableTests();
    } catch (err) {
      console.error('Failed to load suite details:', err);
    }
  };

  const fetchMoreRuns = async () => {
    if (!selectedSuite || loadingMoreRuns) return;
    
    try {
      setLoadingMoreRuns(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/test-suites/${selectedSuite.id}/runs?skip=${allRuns.length}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newRuns = response.data.runs || [];
      setAllRuns(prev => [...prev, ...newRuns]);
      setTotalRunsCount(response.data.total || totalRunsCount);
    } catch (err) {
      console.error('Failed to load more runs:', err);
    } finally {
      setLoadingMoreRuns(false);
    }
  };

  const handleLoadMoreRuns = () => {
    const newCount = displayedRunsCount + 5;
    setDisplayedRunsCount(newCount);
    
    // If we need more runs than we have loaded, fetch more from server
    if (newCount > allRuns.length && allRuns.length < totalRunsCount) {
      fetchMoreRuns();
    }
  };

  useEffect(() => {
    fetchSuites();
  }, [fetchSuites]);

  // Auto-refresh suites when a run completes
  useEffect(() => {
    if (runningSuiteId) {
      const interval = setInterval(() => {
        fetchSuites();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [runningSuiteId, fetchSuites]);

  // Create suite
  const handleCreateSuite = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const createData = {
        ...formData,
        flow_config: formData.suite_type === 'flow_testing' ? flowConfig : null
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/test-suites`, createData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowCreateModal(false);
      setFormData({ name: '', description: '', execution_mode: 'continue_on_failure', suite_type: 'flow_testing' });
      setFlowConfig({
        browser: 'chromium',
        device_type: 'desktop',
        viewport_width: 1920,
        viewport_height: 1080,
        is_headless: true,
        environment_id: null
      });
      fetchSuites();
      
      // Open the newly created suite
      setSelectedSuite(response.data);
      await fetchAvailableTests();
    } catch (err) {
      console.error('Failed to create suite:', err);
      setError('Failed to create test suite');
    }
  };

  // Update suite configuration
  const handleUpdateConfig = async () => {
    if (!selectedSuite) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/test-suites/${selectedSuite.id}`, {
        flow_config: flowConfig
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchSuiteDetails(selectedSuite.id);
      setShowConfigModal(false);
    } catch (err) {
      console.error('Failed to update config:', err);
    }
  };

  // Update suite
  const handleUpdateSuite = async () => {
    if (!selectedSuite) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/test-suites/${selectedSuite.id}`, {
        name: formData.name,
        description: formData.description,
        execution_mode: formData.execution_mode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditMode(false);
      fetchSuites();
      fetchSuiteDetails(selectedSuite.id);
    } catch (err) {
      console.error('Failed to update suite:', err);
      setError('Failed to update test suite');
    }
  };

  // Delete suite
  const handleDeleteSuite = async (suiteId) => {
    if (!window.confirm(t("Are you sure you want to delete this test suite?"))) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/test-suites/${suiteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (selectedSuite?.id === suiteId) {
        setSelectedSuite(null);
      }
      fetchSuites();
    } catch (err) {
      console.error('Failed to delete suite:', err);
      setError('Failed to delete test suite');
    }
  };

  // Add test to suite
  const handleAddTest = async (testId, testType) => {
    if (!selectedSuite) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/test-suites/${selectedSuite.id}/tests`, {
        test_id: testId,
        test_type: testType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchSuiteDetails(selectedSuite.id);
      fetchSuites();
    } catch (err) {
      console.error('Failed to add test:', err);
      if (err.response?.data?.detail) {
        alert(err.response.data.detail);
      }
    }
  };

  // Remove test from suite
  const handleRemoveTest = async (testId) => {
    if (!selectedSuite) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/test-suites/${selectedSuite.id}/tests/${testId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchSuiteDetails(selectedSuite.id);
      fetchSuites();
    } catch (err) {
      console.error('Failed to remove test:', err);
    }
  };

  // Open Add Test modal with fresh data
  const handleOpenAddTestModal = async () => {
    await fetchAvailableTests();
    setShowAddTestModal(true);
  };

  // Run suite
  const handleRunSuite = async (suiteId) => {
    try {
      setRunningSuiteId(suiteId);
      setRunProgress(null);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${BACKEND_URL}/api/test-suites/${suiteId}/run`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const runId = response.data.run_id;
      
      // Poll for results
      const pollInterval = setInterval(async () => {
        try {
          const runResponse = await axios.get(
            `${BACKEND_URL}/api/test-suites/${suiteId}/runs/${runId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          setRunProgress(runResponse.data);
          
          if (runResponse.data.status !== 'running') {
            clearInterval(pollInterval);
            setRunningSuiteId(null);
            setSelectedRun(runResponse.data);
            setShowRunResultModal(true);
            fetchSuites();
            if (selectedSuite?.id === suiteId) {
              fetchSuiteDetails(suiteId);
            }
          }
        } catch (err) {
          console.error('Poll error:', err);
          clearInterval(pollInterval);
          setRunningSuiteId(null);
        }
      }, 2000);
      
    } catch (err) {
      console.error('Failed to run suite:', err);
      setRunningSuiteId(null);
      setError('Failed to start test suite');
    }
  };

  // Format relative time with more precision
  const formatRelativeTime = (dateString) => {
    if (!dateString) return t('Never');
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return t('Just now');
    if (diffMins < 60) return t('{n}m ago', { n: diffMins });
    if (diffHours < 24) {
      const remainingMins = diffMins % 60;
      if (remainingMins === 0) return t('{n}h ago', { n: diffHours });
      return t('{h}h {m}m ago', { h: diffHours, m: remainingMins });
    }
    if (diffDays < 7) {
      const remainingHours = diffHours % 24;
      if (remainingHours === 0) return t('{n}d ago', { n: diffDays });
      return t('{d}d {h}h ago', { d: diffDays, h: remainingHours });
    }
    // For older dates, show the actual date and time
    return date.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter available tests based on suite type
  const filteredTests = (() => {
    // Determine suite type - either explicit or inferred from tests
    let effectiveSuiteType = selectedSuite?.suite_type;
    
    // For legacy suites without a type, infer from existing tests
    if (!effectiveSuiteType && selectedSuite?.tests?.length > 0) {
      const hasFlowTests = selectedSuite.tests.some(t => t.test_type === 'flow_test');
      const hasApiTests = selectedSuite.tests.some(t => t.test_type === 'api_test');
      
      // If only one type of test exists, restrict to that type
      if (hasFlowTests && !hasApiTests) {
        effectiveSuiteType = 'flow_testing';
      } else if (hasApiTests && !hasFlowTests) {
        effectiveSuiteType = 'api_testing';
      }
      // If both types exist or no tests, allow all (legacy behavior)
    }
    
    // Get tests based on suite type
    let testsToFilter = [];
    if (effectiveSuiteType === 'api_testing') {
      testsToFilter = availableApiTests;
    } else if (effectiveSuiteType === 'flow_testing') {
      testsToFilter = availableFlowTests;
    } else {
      // For mixed or unknown suites, show all
      testsToFilter = [...availableApiTests, ...availableFlowTests];
    }
    
    return testsToFilter.filter(test => {
      const matchesSearch = !testSearchQuery || 
        test.name.toLowerCase().includes(testSearchQuery.toLowerCase());
      const matchesType = testTypeFilter === 'all' || test.type === testTypeFilter;
      const alreadyInSuite = selectedSuite?.tests?.some(t => t.test_id === test.id);
      
      return matchesSearch && matchesType && !alreadyInSuite;
    });
  })();
  
  // Determine effective suite type for UI display
  const getEffectiveSuiteType = () => {
    if (selectedSuite?.suite_type) return selectedSuite.suite_type;
    if (!selectedSuite?.tests?.length) return null;
    
    const hasFlowTests = selectedSuite.tests.some(t => t.test_type === 'flow_test');
    const hasApiTests = selectedSuite.tests.some(t => t.test_type === 'api_test');
    
    if (hasFlowTests && !hasApiTests) return 'flow_testing';
    if (hasApiTests && !hasFlowTests) return 'api_testing';
    return null; // mixed
  };

  if (loading && suites.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="lexa-suite-page flex flex-col lg:flex-row min-h-[300px] lg:h-[calc(100vh-180px)]">
      {/* Left Panel - Suites List */}
      <div className={`lexa-suite-list ${selectedSuite ? 'w-full lg:w-80 lg:shrink-0 lg:border-r' : 'w-full'} flex flex-col bg-white transition-all duration-200`}>
        {/* Header */}
        <div className="lexa-suite-list-header p-4 border-b">
          <div className="flex items-center justify-between mb-3">
            <h1 className="lexa-suite-title text-lg font-semibold text-gray-900 flex items-center">
              <Layers className="w-5 h-5 mr-2 text-indigo-600" />
              {t("Test Suites")}
            </h1>
            <Button size="sm" className="lexa-suite-primary-action" onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-1" />
              {t("New")}
            </Button>
          </div>
          {!selectedSuite && (
            <p className="text-sm text-gray-500">
              {t("Organize and run multiple tests in sequence")}
            </p>
          )}
        </div>

        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center text-sm">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="flex-1">{t(error)}</span>
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Suites List */}
        <div className="lexa-suite-items flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-500 mx-auto mb-3 animate-spin" />
              <p className="text-sm text-gray-500">{t("Loading test suites...")}</p>
            </div>
          ) : suites.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">{t("No Test Suites")}</h3>
              <p className="text-xs text-gray-500 mb-4">{t("Create your first suite to batch run tests")}</p>
              <Button size="sm" className="lexa-suite-primary-action" onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-1" />
                {t("Create Suite")}
              </Button>
            </div>
          ) : (
            suites.map(suite => (
              <div
                key={suite.id}
                onClick={() => fetchSuiteDetails(suite.id)}
                className={`lexa-suite-row p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedSuite?.id === suite.id
                    ? 'lexa-suite-row--selected border-indigo-500 bg-indigo-50'
                    : 'lexa-suite-row--idle border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start flex-1 min-w-0">
                    {/* Suite Type Icon */}
                    <div className={`p-1.5 rounded mr-2 ${
                      suite.suite_type === 'api_testing' ? 'bg-green-100' : 'bg-indigo-100'
                    }`}>
                      {suite.suite_type === 'api_testing' ? (
                        <FlaskConical className="w-4 h-4 text-green-600" />
                      ) : (
                        <Workflow className="w-4 h-4 text-indigo-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{suite.name}</h3>
                      <div className="flex items-center mt-1 text-xs text-gray-500 space-x-3">
                        <span className="flex items-center">
                          <ListChecks className="w-3 h-3 mr-1" />
                          {suite.tests?.length || 0}
                        </span>
                        {suite.last_run && (
                          <>
                            <span className="flex items-center">
                              <StatusIndicator status={suite.last_run.status} size="sm" />
                              <span className="ml-1 capitalize">{t(suite.last_run.status)}</span>
                            </span>
                            <span>{formatRelativeTime(suite.last_run.started_at)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {runningSuiteId === suite.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </div>
                
                {/* Run progress bar */}
                {runningSuiteId === suite.id && runProgress && (
                  <div className="mt-2">
                    <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-indigo-600 transition-all duration-300"
                        style={{ 
                          width: `${((runProgress.summary?.passed + runProgress.summary?.failed || 0) / (runProgress.summary?.total || 1)) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Suite Details */}
      {selectedSuite && (
        <div className="lexa-suite-detail flex-1 flex flex-col bg-gray-50 overflow-hidden">
          {/* Suite Header */}
          <div className="lexa-suite-detail-header p-4 bg-white border-b">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex-1">
                {editMode ? (
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="text-lg font-semibold mb-2"
                    autoFocus
                  />
                ) : (
                  <h2 className="text-lg font-semibold text-gray-900">{selectedSuite.name}</h2>
                )}
                {editMode ? (
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t("Add description...")}
                    className="text-sm"
                  />
                ) : (
                  <p className="text-sm text-gray-500">{selectedSuite.description || t("No description")}</p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {editMode ? (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setEditMode(false)}>{t("Cancel")}</Button>
                    <Button size="sm" onClick={handleUpdateSuite}>{t("Save")}</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" className="lexa-suite-primary-action"
                      onClick={() => handleRunSuite(selectedSuite.id)}
                      disabled={runningSuiteId === selectedSuite.id || !selectedSuite.tests?.length}
                    >
                      {runningSuiteId === selectedSuite.id ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-1" />
                      )}
                      {t("Run")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={t("Edit suite")}
                      onClick={() => {
                        setFormData({
                          name: selectedSuite.name,
                          description: selectedSuite.description || '',
                          execution_mode: selectedSuite.execution_mode,
                          suite_type: selectedSuite.suite_type || 'flow_testing'
                        });
                        setEditMode(true);
                      }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {/* Configuration button ONLY for flow testing suites */}
                    {(selectedSuite.suite_type === 'flow_testing' || 
                      (!selectedSuite.suite_type && selectedSuite.tests?.some(t => t.test_type === 'flow_test'))
                    ) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setFlowConfig(selectedSuite.flow_config || {
                            browser: 'chromium',
                            device_type: 'desktop',
                            viewport_width: 1920,
                            viewport_height: 1080,
                            is_headless: true,
                            environment_id: null
                          });
                          setShowConfigModal(true);
                        }}
                        title={t("Execution Configuration")}
                      >
                        <Settings2 className="w-4 h-4" />
                        <Workflow className="w-3 h-3 ml-1 text-indigo-500" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={t("Delete suite")}
                      onClick={() => handleDeleteSuite(selectedSuite.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t("Close")}
                      onClick={() => setSelectedSuite(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {/* Suite stats */}
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
              <span className="flex items-center text-gray-600">
                <Settings2 className="w-4 h-4 mr-1" />
                {selectedSuite.execution_mode === 'stop_on_failure' ? t("Stop on failure") : t("Run all tests")}
              </span>
              {selectedSuite.last_run && (
                <span className="flex items-center">
                  <StatusIndicator status={selectedSuite.last_run.status} />
                  <span className="ml-1 text-gray-600">
                    {t("Last:")} {selectedSuite.last_run.summary?.passed}/{selectedSuite.last_run.summary?.total} {t("passed")}
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* Tests in Suite */}
          <div className="lexa-suite-detail-body flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-700">
                {t("Tests (")}{selectedSuite.tests?.length || 0})
              </h3>
              <Button size="sm" variant="outline" className="lexa-suite-primary-action" onClick={handleOpenAddTestModal}>
                <Plus className="w-4 h-4 mr-1" />
                {t("Add Test")}
              </Button>
            </div>

            {!selectedSuite.tests?.length ? (
              <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed border-gray-200">
                <ListChecks className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-3">{t("No tests in this suite yet")}</p>
                <Button size="sm" className="lexa-suite-primary-action" onClick={handleOpenAddTestModal}>
                  <Plus className="w-4 h-4 mr-1" />
                  {t("Add Tests")}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedSuite.tests.sort((a, b) => a.order - b.order).map((test, index) => (
                  <div 
                    key={test.test_id}
                    className="flex items-center p-3 bg-white rounded-lg border border-gray-200 group hover:border-gray-300"
                  >
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-medium text-gray-600 mr-3">
                      {index + 1}
                    </div>
                    <div className={`p-1.5 rounded ${test.test_type === 'api_test' ? 'bg-blue-100' : 'bg-purple-100'} mr-3`}>
                      {test.test_type === 'api_test' ? (
                        <FlaskConical className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Workflow className="w-4 h-4 text-purple-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{test.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{language === 'es' ? t(test.test_type) : test.test_type.replace('_', ' ')}</p>
                    </div>
                    <Button 
                      size="sm"
                      variant="ghost"
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={t("Remove test")}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveTest(test.test_id);
                      }}
                    >
                      <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Recent Runs */}
            {allRuns.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium text-gray-700 mb-3 flex items-center justify-between">
                  <span className="flex items-center">
                    <History className="w-4 h-4 mr-2" />
                    {t("Run History")}
                  </span>
                  <span className="text-xs text-gray-400 font-normal">
                    {t(totalRunsCount === 1 ? '{n} total run' : '{n} total runs', { n: totalRunsCount })}
                  </span>
                </h3>
                <div className="space-y-2">
                  {allRuns.slice(0, displayedRunsCount).map((run, index) => (
                    <div 
                      key={run.id}
                      onClick={() => {
                        setSelectedRun(run);
                        setShowRunResultModal(true);
                      }}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-xs text-gray-400 w-6 text-right font-mono">
                          #{totalRunsCount - index}
                        </span>
                        <StatusIndicator status={run.status} />
                        <span className="text-sm font-medium capitalize">{t(run.status)}</span>
                        <span className="text-sm text-gray-500">
                          {run.summary?.passed}/{run.summary?.total} {t("passed")}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {formatRelativeTime(run.started_at)}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Load More Button */}
                {(displayedRunsCount < allRuns.length || allRuns.length < totalRunsCount) && (
                  <div className="mt-3 text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleLoadMoreRuns}
                      disabled={loadingMoreRuns}
                      className="w-full"
                    >
                      {loadingMoreRuns ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t("Loading...")}
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          {t("Load More (")}{Math.min(displayedRunsCount, allRuns.length)} {t("of")} {totalRunsCount})
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <SuiteCreateDialog
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        formData={formData}
        setFormData={setFormData}
        flowConfig={flowConfig}
        setFlowConfig={setFlowConfig}
        availableEnvironments={availableEnvironments}
        handleCreateSuite={handleCreateSuite}
      />

      <SuiteAddTestsDialog
        showAddTestModal={showAddTestModal}
        selectedSuite={selectedSuite}
        setShowAddTestModal={setShowAddTestModal}
        getEffectiveSuiteType={getEffectiveSuiteType}
        testSearchQuery={testSearchQuery}
        setTestSearchQuery={setTestSearchQuery}
        testTypeFilter={testTypeFilter}
        setTestTypeFilter={setTestTypeFilter}
        filteredTests={filteredTests}
        handleAddTest={handleAddTest}
      />

      <SuiteRunResultsDialog
        showRunResultModal={showRunResultModal}
        selectedRun={selectedRun}
        formatRelativeTime={formatRelativeTime}
        setShowRunResultModal={setShowRunResultModal}
        setSelectedRun={setSelectedRun}
      />

      <SuiteConfigDialog
        showConfigModal={showConfigModal}
        selectedSuite={selectedSuite}
        setShowConfigModal={setShowConfigModal}
        flowConfig={flowConfig}
        setFlowConfig={setFlowConfig}
        availableEnvironments={availableEnvironments}
        handleUpdateConfig={handleUpdateConfig}
      />


    </div>
  );
};

export default TestSuites;
